import {
  IconButton,
  makeStyles,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  useTheme
} from '@material-ui/core';
import Table from '@material-ui/core/Table';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';
import React, { ChangeEvent, useEffect, useState } from 'react';
import create, { UseStore } from 'zustand';

type ColumnStyle = {
  align?: 'right' | 'left' | 'center';
  padding?: 'none' | 'default';
  width?: number | string;
};

export type TableColumn<T = any> = {
  label?: string;
  id?: string;
  sort?: boolean;
  render?: (row: T) => any;
  style?: ColumnStyle;
  classes?: any;
  data?: any;
  role?: "row"
};

type Page = {
  pageNumber: number;
  pageSize: number;
};

export type PageApi = UseStore<Page>;
export function createPageApi(): PageApi {
  return create<Page>(() => ({
    pageNumber: 0,
    pageSize: 20,
  }));
}

export type TableProps<T = any> = {
  columns: TableColumn<T>[];
  rows: T[];
  pageApi?: UseStore<Page>;
  onRequestSort?: (event: any, column: TableColumn<T>) => void;
  onRequestPage?: (page: Page) => void;
  onRowClick?: (row: any) => void;
  classes?: any;
  totalResults?: number;
  containerWidth?: number | string;
  containerHeight?: number | string;
  rowIsSelected?: (rowData: T) => boolean;
  navigationStyle?: React.CSSProperties;
  id?: string;
};

interface HeaderProps<T = any> {
  onRequestSort?: (event: any, column: TableColumn<T>) => void;
  columns?: TableColumn[];
  column?: TableColumn;
  classes?: any;
}

function HeaderCell(props: HeaderProps) {
  const { column, onRequestSort, classes } = props;
  const { align, width } = column.style || {};
  const [sortOrder, setSortOrder] = useState(1);
  const direction = sortOrder === -1 ? 'desc' : 'asc';

  const onClick = () => {
    const order = sortOrder === -1 ? 1 : -1;
    setSortOrder(order);
    if (onRequestSort) {
      onRequestSort(order, column);
    }
   
  };

  

  function checkSort(){
    if(typeof(column.data?.sortable) === 'boolean'){
      return column.data.sortable;
    }
    
    return column.sort;
    
  }


 

  return (
    <TableCell
      key={column.id}
      align={align || 'left'}
      padding={'none'}
      width={width}
      style={{ paddingLeft: 0, paddingTop: 4, paddingBottom: 4 }}
      sortDirection={direction}
      data-field={column?.label}
    >
      {checkSort() ? (
        <TableSortLabel direction={direction} onClick={onClick}>
          {column.label}

          <span className={classes.visuallyHidden}>
            {direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
          </span>
        </TableSortLabel>
      ) : (
        column.label
      )}
    </TableCell>
  );
}

function Head(props: HeaderProps) {
  const { columns, onRequestSort, classes } = props;
  return (
    <TableHead role="rowhead">
      <TableRow>
        {columns.map((column, i) => (
          <HeaderCell
            key={i}
            classes={classes}
            column={column}
            onRequestSort={onRequestSort}
          />
        ))}
      </TableRow>
    </TableHead>
  );
}

const paginationStyles = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));
function TablePaginationActions(props: any) {
  const { count, page, rowsPerPage, onPageChange } = props;
  const classes = paginationStyles();
  const theme = useTheme();

  const onFirstPage = (e: any) => {
    onPageChange(e, 0);
  };

  const onBack = (e: any) => {
    onPageChange(e, page - 1);
  };

  const onNext = (e: any) => {
    onPageChange(e, page + 1);
  };

  const onLastPage = (e: any) => {
    onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton onClick={onFirstPage} disabled={page === 0}>
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={onBack} disabled={page === 0}>
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={onNext}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={onLastPage}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

function Pagination(props: TableProps) {
  const { onRequestPage, totalResults, pageApi, navigationStyle } = props;
  const [page, setPage] = useState(pageApi.getState().pageNumber || 0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const onChangePage = (_: any | null, newPage: number) => {
    setPage(newPage);
    pageApi.setState({ pageNumber: newPage });
    onRequestPage({ pageNumber: newPage, pageSize: rowsPerPage });
  };

  const onChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const pageSize = parseInt(event.target.value, 10);
    onRequestPage({ pageNumber: page, pageSize });
    setRowsPerPage(pageSize);
  };

  useEffect(() => {
    if (pageApi) {
      return pageApi.subscribe((v) => {
        setPage(v?.pageNumber || 0);
      });
    }
  }, []);

  return (
    <TablePagination
      ActionsComponent={TablePaginationActions}
      rowsPerPageOptions={[20, 40, 60, 100]}
      component="div"
      count={totalResults || 0}
      page={page}
      onPageChange={onChangePage}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      style={navigationStyle}
    />
  );
}

function Row(row: any, props: TableProps, j: number) {
  const { columns, rowIsSelected, onRowClick } = props;
  
  const rowStyle: any = rowIsSelected
    ? rowIsSelected(row)
      ? { backgroundColor: '#f0f0f0' }
      : undefined
    : undefined;

  const onDoubleClick = () => {
    if (onRowClick) {
      onRowClick(row);
    }
  };
  return (
    
    <TableRow key={j} style={rowStyle} onDoubleClick={onDoubleClick} hover={true} role="row" data-rowindex={j}>
      {columns.map((column, i) => {
        const { render, id, label } = column;
        const { align, padding, width } = column.style || {};
        const rowId = (props.id || label || 'table') + '-row' + j + '-col' + i;

        return (
          <TableCell
            key={rowId}
            id={rowId}
            align={align || 'justify'}
            padding={padding || 'none'}
            style={{ width, paddingTop: 0, paddingBottom: 0 }}
            role='cell'
            data-field={column?.label}
          >
            {render ? render(row) :  row[id]}
          </TableCell>
        );
      })}
    </TableRow>
   
  );
}

const useTableStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

export function PagedTable(p: TableProps) {
  const props = {
    ...{
      classes: useTableStyles(),
      containerHeight: '80vh',
      containerWidth: '100%',
    },
    ...p,
  } as TableProps;
  const { columns, onRequestSort, rows, classes, id } = props;

  return (
    <Paper>
      <TableContainer
        style={{
          maxHeight: props.containerHeight || 500,
          width: props.containerWidth,
        }}
      >
        <Table id={id} stickyHeader className={classes.table} size="medium" role="grid">
          <Head
            columns={columns}
            onRequestSort={onRequestSort}
            classes={classes}
          />
          <TableBody role="rowgroup">{(rows || []).map((v, i) => Row(v, props, i))}</TableBody>
        </Table>
      </TableContainer>
      <Pagination {...props} />
    </Paper>
  );
}
